import { useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/components/ui/use-toast";
import { cn } from "@/infrastructure/lib/utils";
import { Download, Settings } from "lucide-react";
import { useState } from "react";
import { AgentBaseService } from "../services/agentBaseService";
import { Card, CardContent } from "@/shared/components/ui/card";
import { AgentInstallerWizard } from "./AgentInstaller/AgentInstallerWizard";
import { InstallationResult } from "../types/AgentInstaller";
import { translateDeep } from "@/shared/utils/i18nHelper";
import { analyticsService } from "@/shared/services/analytics";

interface Integration {
  id: string;
  name: string;
  label?: string;
  logo_url: string | null;
}

// Integração embutida no agente pelo backend v2 (include_integrations).
interface EmbeddedAgentIntegration {
  agent_integration_id: string;
  integration_base_id: string;
  name: string | null;
  label: string | null;
  logo_url: string | null;
}

interface AgentWithIntegrations {
  id: string;
  name: string;
  description?: string;
  status: string; // Novo campo: 'installed', 'available', 'active', 'inactive', 'draft', 'error'
  is_public?: boolean;
  product_id?: string;
  created_at?: string;
  updated_at?: string;
  type?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings?: any;
  logo_url?: string;
  avatar_url?: string;
  agent_id?: string; // ID do agente na tabela agents (usado para agentes não instalados)
  integrations?: EmbeddedAgentIntegration[]; // embutido pelo v2 (include_integrations)
  linkedIntegrations: Integration[];
}

/**
 * Iniciais para o fallback do logo. Fora do componente porque não depende de
 * props nem de estado: dentro, era uma função nova a cada render e, estando na
 * lista de dependências do useMemo da lista, anulava a memoização — era o que o
 * `eslint-disable` ali escondia.
 */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AgentBase = () => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Obter idioma atual do usuário
  const currentLang = i18n.language.split("-")[0]; // 'pt' ou 'en'

  // Estados - devem estar no topo
  const [_installingAgentId, _setInstallingAgentId] = useState<string | null>(
    null,
  );
  const [_installProgress, _setInstallProgress] = useState<number>(0);
  const [selectedAgent, setSelectedAgent] =
    useState<AgentWithIntegrations | null>(null);
  const [isInstallerOpen, setIsInstallerOpen] = useState(false);
  // URLs de logo que falharam ao carregar. Por estado, e não por `innerHTML` no
  // elemento pai: aquele nó é do React, e reescrevê-lo por fora faz o próximo
  // render da lista duplicar ou apagar a inicial.
  //
  // Chaveado pela URL, não pelo id do agente: uma falha transitória de rede
  // marcava o agente para sempre, e trocar o logo não dava nova chance. Com a
  // URL, editar o logo já é um valor novo, e a lista é limpa quando o conjunto
  // de agentes muda.
  const [failedLogoUrls, setFailedLogoUrls] = useState<Set<string>>(new Set());

  const {
    data: agentsQueryData,
    isLoading,
    isError,
    refetch,
  } = useQuery<AgentWithIntegrations[]>({
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    queryKey: ["agent_base", currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      // Erro sobe para o react-query: devolver [] fazia falha de rede virar
      // "nenhum agente disponível" na tela, que é o mesmo texto do caso legítimo.
      // organization_id já vem no contexto do workspace — sem request extra.
      const organizationId = currentWorkspace.organization_id;

      if (!organizationId) {
        return [];
      }
      const { data: agentsData, error: agentsError } =
        await AgentBaseService.getByProductId(currentWorkspace.id);

      if (agentsError) {
        throw agentsError;
      }

      if (!agentsData || agentsData.length === 0) {
        return [];
      }

      // Remover duplicatas (quando um agente aparece como installed e available)
      // Usa nome + product_id como identificador único mais confiável
      const agentMap = new Map();

      // Primeiro, priorizamos agentes instalados
      agentsData.forEach((agent) => {
        // Use uma combinação mais robusta como identificador
        // Para agentes instalados: usa o agent_id se disponível, senão o name
        // Para agentes disponíveis: usa o agent_id
        const agentIdentifier = agent.agent_id
          ? `agent_${agent.agent_id}`
          : `name_${agent.name}_${agent.product_id || "default"}`;

        // Se o agente já existe no Map, aplicar regras de prioridade
        if (agentMap.has(agentIdentifier)) {
          const existingAgent = agentMap.get(agentIdentifier);

          // Priorizar agentes instalados sobre disponíveis
          if (
            agent.status === "installed" &&
            existingAgent.status !== "installed"
          ) {
            agentMap.set(agentIdentifier, agent);
          } else if (
            agent.status === "installed" &&
            existingAgent.status === "installed"
          ) {
            // Se ambos são installed, manter o primeiro (mais antigo)
          } else if (
            agent.status !== "installed" &&
            existingAgent.status !== "installed"
          ) {
            // Se nenhum é installed, manter o com status de maior prioridade
            const statusPriority: Record<string, number> = {
              available: 1,
              soon: 2,
              draft: 3,
            };
            const currentPriority = statusPriority[agent.status] || 99;
            const existingPriority =
              statusPriority[existingAgent.status] || 99;

            if (currentPriority < existingPriority) {
              agentMap.set(agentIdentifier, agent);
            }
          }
        } else {
          // Se não existe, adicionamos
          agentMap.set(agentIdentifier, agent);
        }
      });

      // Converter o Map de volta para array
      const deduplicatedAgents = Array.from(agentMap.values());

      return deduplicatedAgents.map((agent) => {
        // As integrações já vêm embutidas no agente (include_integrations).
        const embeddedIntegrations: EmbeddedAgentIntegration[] =
          agent.integrations ?? [];
        const linkedIntegrations: Integration[] = embeddedIntegrations.map(
          (i) => ({
            id: i.integration_base_id,
            name: i.name || i.label || "",
            label: i.label || i.name || "",
            logo_url: i.logo_url ?? null,
          }),
        );
        return { ...agent, linkedIntegrations };
      });
    },
    enabled: !!currentWorkspace?.id,
  });

  // Em useMemo porque o `?? []` cria um array novo a cada render, e este valor é
  // dependência do useMemo da lista — sem isto a memoização não vale nada.
  const agents = useMemo(() => agentsQueryData ?? [], [agentsQueryData]);

  // Função para abrir o wizard de instalação (memoizada)
  const handleInstallAgent = useCallback(
    (agent: AgentWithIntegrations) => {
      if (!currentWorkspace?.id || !user?.id) {
        toast({
          title: t("common.error"),
          description: t("agents.errors.missingInfo"),
          variant: "destructive",
        });
        return;
      }

      setSelectedAgent(agent);
      setIsInstallerOpen(true);
    },
    [currentWorkspace?.id, user?.id, toast, t],
  );

  // 🔄 Função para atualizar lista de agentes (memoizada para evitar re-renders)
  const handleRefreshAgentList = useCallback(async () => {
    // Invalidar cache e fazer refetch (com throttling)
    await queryClient.invalidateQueries({
      queryKey: ["agent_base", currentWorkspace?.id],
      exact: true,
    });

    // Aguardar um pouco para evitar múltiplas chamadas simultâneas
    await new Promise((resolve) => setTimeout(resolve, 200));

    await refetch();
  }, [currentWorkspace?.id, queryClient, refetch]);

  // Lista nova, chance nova: uma falha transitória de rede não pode marcar o
  // logo para o resto da sessão.
  //
  // Depende de `agentsQueryData`, o valor cru da query — e não do `agents` com
  // default `[]`, que é um array novo a cada render enquanto a query não resolve
  // e fazia este efeito chamar setState em todo render.
  useEffect(() => {
    setFailedLogoUrls(new Set());
  }, [agentsQueryData]);

  // Memoizar a lista de agentes renderizada

  const renderedAgents = useMemo(() => {
    if (!agents) return [];

    return [...agents]
      .sort((a, b) => {
        // Definir prioridade de status
        const statusPriority: Record<string, number> = {
          installed: 1,
          available: 2,
          soon: 3,
        };

        // Obter prioridade ou valor alto para status desconhecidos
        const priorityA = statusPriority[a.status] || 99;
        const priorityB = statusPriority[b.status] || 99;

        // Ordenar por prioridade
        return priorityA - priorityB;
      })
      .map((agent) => {
        // Traduzir settings do agent de acordo com o idioma do usuário
        const translatedSettings = agent.settings
          ? translateDeep(agent.settings, currentLang)
          : agent.settings;

        return (
          <Card
            key={agent.agent_id}
            className={cn(
              "group hover:shadow-lg transition-all duration-200",
              "hover:scale-[1.02]",
              "relative",
              "border border-border/50",
            )}
          >
            {/* Integrations Logos */}
            {agent.linkedIntegrations?.length > 0 && (
              <div className="absolute top-3 left-3 flex -space-x-2">
                {agent.linkedIntegrations.slice(0, 3).map((integration) => (
                  <div
                    key={integration.id}
                    className="relative group/integration"
                  >
                    <div className="w-8 h-8 rounded-full bg-background border border-border/50 p-1 shadow-sm hover:scale-110 transition-transform relative z-10">
                      {integration.logo_url ? (
                        <img
                          src={integration.logo_url}
                          alt={integration.name}
                          className="w-full h-full object-contain rounded-full"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-primary/5 rounded-full">
                          <Settings className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="pointer-events-none absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md opacity-0 group-hover/integration:opacity-100 transition-opacity whitespace-nowrap z-[100] shadow-md">
                      {integration.label || integration.name}
                    </div>
                  </div>
                ))}
                {agent.linkedIntegrations.length > 3 && (
                  <div
                    className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground relative z-10"
                    title={t("agents.moreIntegrations", {
                      count: agent.linkedIntegrations.length - 3,
                    })}
                  >
                    +{agent.linkedIntegrations.length - 3}
                  </div>
                )}
              </div>
            )}

            <div className="absolute top-3 right-3 flex gap-2">
              {agent.status === "installed" ? (
                <button
                  className="text-muted-foreground hover:text-primary p-1.5 rounded-md hover:bg-muted transition-all duration-300 hover:shadow-sm"
                  onClick={(e) => {
                    e.stopPropagation();

                    // Track agent viewed
                    analyticsService.trackEvent("agent_viewed", {
                      agent_name: agent.name,
                      agent_id: agent.id,
                      source: "list",
                    });

                    navigate(`/agents/${agent.id}/settings`);
                  }}
                >
                  <Settings className="h-4 w-4" />
                </button>
              ) : agent.status === "available" ? (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "bg-background/80 hover:bg-background flex items-center gap-1 relative overflow-hidden",
                    "shadow-sm hover:shadow-md transition-all duration-300 border-primary/20 hover:border-primary",
                    "rounded-full px-4 hover:scale-105 hover:bg-primary/5",
                    _installingAgentId === (agent.agent_id || agent.id) &&
                      "cursor-wait",
                  )}
                  onClick={() => handleInstallAgent(agent)}
                  disabled={_installingAgentId === (agent.agent_id || agent.id)}
                  data-tour="install-agent-button"
                >
                  {_installingAgentId === (agent.agent_id || agent.id) && (
                    <div
                      className="absolute left-0 top-0 bottom-0 bg-primary/30 z-0"
                      style={{
                        width: `${_installProgress}%`,
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                  )}
                  <Download className="h-3.5 w-3.5 relative z-10 text-primary group-hover:text-primary" />
                  <span className="relative z-10 font-medium">
                    {_installingAgentId === (agent.agent_id || agent.id)
                      ? t("agents.installing")
                      : t("agents.install")}
                  </span>
                </Button>
              ) : (
                agent.status === "soon" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-background/80 hover:bg-background gap-1 rounded-full px-4 opacity-80 cursor-not-allowed"
                    disabled={true}
                  >
                    <span className="font-medium">
                      {t("agents.comingSoon")}
                    </span>
                  </Button>
                )
              )}
            </div>

            <CardContent className="pt-12 p-6">
              <div className="flex flex-col items-center">
                {/* Avatar e Status */}
                <div className="relative">
                  <div className="h-16 w-16 rounded-full bg-primary/5 ring-4 ring-background shadow-xl overflow-hidden">
                    {(agent.logo_url || agent.avatar_url) &&
                    !failedLogoUrls.has(
                      agent.logo_url || agent.avatar_url || "",
                    ) ? (
                      <div className="w-full h-full">
                        <img
                          src={agent.logo_url || agent.avatar_url}
                          alt={agent.name}
                          className="w-full h-full object-cover"
                          onError={() =>
                            setFailedLogoUrls((previous) =>
                              new Set(previous).add(
                                agent.logo_url || agent.avatar_url || "",
                              ),
                            )
                          }
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-medium text-lg">
                        {getInitials(agent.name)}
                      </div>
                    )}
                  </div>
                  {agent.status === "installed" && (
                    <div
                      className="absolute bottom-0.5 right-0.5 h-4 w-4 rounded-full border-2 border-background bg-green-500"
                      title={`Status: ${agent.status}`}
                    />
                  )}
                </div>

                {/* Nome e Versão */}
                <div className="mt-6 text-center">
                  <h3 className="font-semibold text-lg">{agent.name}</h3>
                  {translatedSettings?.basic?.version && (
                    <p className="text-sm text-muted-foreground mt-1">
                      v{translatedSettings.basic.version}
                    </p>
                  )}
                </div>

                {/* Descrição */}
                {translatedSettings?.basic?.description && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground line-clamp-2 max-w-[280px] mx-auto">
                      {translatedSettings.basic.description}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      });
  }, [
    agents,
    handleInstallAgent,
    navigate,
    t,
    _installingAgentId,
    _installProgress,
    currentLang,
    // Sem isto o fallback do logo nunca apareceria: a lista é memoizada e o
    // `eslint-disable` acima impede o lint de cobrar a dependência.
    failedLogoUrls,
  ]);

  // 🎉 Callback quando instalação é concluída (simplificado)
  const handleInstallComplete = async (result: InstallationResult) => {
    if (!result.success) {
      toast({
        title: t("common.error"),
        description: result.message || t("agents.errors.installFailed"),
        variant: "destructive",
      });
    }

    // Fechar o wizard
    setIsInstallerOpen(false);
    setSelectedAgent(null);
  };

  // 🚫 Callback quando wizard é fechado
  const handleInstallerClose = () => {
    setIsInstallerOpen(false);
    setSelectedAgent(null);
  };

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-muted-foreground">
        {t("agents.selectWorkspace")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Skeleton loading cards */}
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-32 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-6">
          <p className="text-destructive">
            {t("common.messages.errorDescription")}
          </p>
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-muted-foreground">{t("agents.noAgents")}</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {renderedAgents}
        </div>
      )}

      {/* 🧙‍♂️ Agent Installer Wizard */}
      {selectedAgent && (
        <AgentInstallerWizard
          agent={selectedAgent}
          isOpen={isInstallerOpen}
          onClose={handleInstallerClose}
          onInstallComplete={handleInstallComplete}
          onRefreshAgentList={handleRefreshAgentList}
        />
      )}
    </div>
  );
};

export default AgentBase;
